<template>
  <div class="google-address-typeahead">
    <!--
      Admin theme: DOM nesting matches VAutocomplete.vue exactly - .v-autocomplete__list's CSS
      (resources/sass/admin/blocks/_v-autocomplete.scss) uses an absolute `top: 50px` and
      `z-index: 4` that only land correctly when the list is a DIRECT CHILD of the same
      `.v-autocomplete` (position:relative) ancestor that also contains the input - a list
      placed merely AFTER the input as a separate wrapper (as the public theme structure below
      does) stacks that 50px offset on top of the wrong reference point, and traps the z-index
      inside the input's own `.v-input` stacking context (also z-index:1) instead of escaping to
      compare against sibling fields on the page - both bugs this exact structure fixes.
    -->
    <div class="v-autocomplete" v-if="theme === 'admin'">
      <div class="v-autocomplete__input">
        <div class="v-input">
          <input
            type="text"
            class="v-input__field"
            :placeholder="labels.placeholder"
            v-model="query"
            @input="onQueryInput"
            @keydown="onInputKeydown"
            role="combobox"
            aria-autocomplete="list"
            :aria-expanded="showList ? 'true' : 'false'"
            :aria-controls="listId"
          />
        </div>
      </div>
      <ul
        v-if="showList && suggestions.length"
        :id="listId"
        class="v-autocomplete__list"
        role="listbox"
        :aria-label="labels.listboxLabel"
      >
        <li
          v-for="(candidate, index) in suggestions"
          :key="candidate.id"
          :id="optionId(index)"
          class="v-autocomplete__item"
          :class="{ 'v-autocomplete__item--is-selected': index === highlightedIndex }"
          role="option"
          :aria-selected="index === highlightedIndex ? 'true' : 'false'"
          @mousedown.prevent="selectCandidate(candidate)"
          @touchstart.prevent="selectCandidate(candidate)"
        >
          {{ candidate.label }}
        </li>
      </ul>
    </div>

    <!-- Public theme: unchanged from before - matches PdokAddressTypeahead.vue's own structure,
         whose .theme-autocomplete__list CSS uses a relative `top: 100%` (not a hardcoded px
         value), so it's already tolerant of the list sitting in its own wrapper after the input. -->
    <div class="theme-autocomplete__field-holder" v-else>
      <input
        type="text"
        class="theme-input"
        :placeholder="labels.placeholder"
        v-model="query"
        @input="onQueryInput"
        @keydown="onInputKeydown"
        role="combobox"
        aria-autocomplete="list"
        :aria-expanded="showList ? 'true' : 'false'"
        :aria-controls="listId"
      />
      <div class="theme-autocomplete">
        <ul
          v-if="showList && suggestions.length"
          :id="listId"
          class="theme-autocomplete__list"
          role="listbox"
          :aria-label="labels.listboxLabel"
        >
          <li
            v-for="(candidate, index) in suggestions"
            :key="candidate.id"
            :id="optionId(index)"
            class="theme-autocomplete__item"
            :class="{ 'theme-autocomplete__item--is-selected': index === highlightedIndex }"
            role="option"
            :aria-selected="index === highlightedIndex ? 'true' : 'false'"
            @mousedown.prevent="selectCandidate(candidate)"
            @touchstart.prevent="selectCandidate(candidate)"
          >
            {{ candidate.label }}
          </li>
        </ul>
      </div>
    </div>

    <div v-if="message" class="text-sm mt-1" style="color: red" role="status" aria-live="polite">
      {{ message }}
    </div>

    <!--
      Confirm/Fix outcome - Google's own guidance: a correction is never auto-applied. The
      suggested fields are shown for explicit accept, or the user can keep their own entry and
      continue manually (see InternationalAddressField.vue's manual-entry branch).
    -->
    <div v-if="pendingConfirmation" class="google-address-typeahead__confirm" role="status">
      <p class="text-sm">{{ labels.confirmPrompt }}</p>
      <p class="text-sm" style="font-weight: bold">{{ confirmationSummary }}</p>
      <div class="google-address-typeahead__confirm-actions">
        <button type="button" :class="classes.buttonPrimary" @click="acceptSuggestion">
          {{ labels.acceptSuggestion }}
        </button>
        <button type="button" :class="classes.buttonSecondary" @click="rejectSuggestion">
          {{ labels.keepMyEntry }}
        </button>
      </div>
    </div>

    <!-- Required Google Maps Platform attribution - see
         https://developers.google.com/maps/documentation/places/web-service/policies -->
    <div class="google-address-typeahead__attribution text-sm" style="color: #999" v-if="suggestions.length || pendingConfirmation">
      {{ labels.attribution }}
    </div>
  </div>
</template>

<script>
import { generateSessionToken } from '../helpers/googleSessionToken';

function csrfToken() {
  var meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute('content') : '';
}

var MESSAGES = {
  nl: {
    placeholder: 'Begin met typen om het adres te zoeken...',
    listboxLabel: 'Adressuggesties',
    unavailable: 'Adressuggesties zijn tijdelijk niet beschikbaar; voer het adres handmatig in.',
    noResults: 'Geen adressen gevonden.',
    invalidSelection: 'Deze suggestie kon niet worden verwerkt; voer het adres handmatig in.',
    incomplete: 'Dit adres is onvolledig; probeer het te verfijnen of vul het handmatig aan.',
    confirmPrompt: 'We konden dit adres niet volledig bevestigen. Bedoelde u:',
    acceptSuggestion: 'Ja, gebruik dit adres',
    keepMyEntry: 'Nee, ik vul het handmatig in',
    attribution: 'Adresgegevens door Google',
  },
  en: {
    placeholder: 'Start typing to search the address...',
    listboxLabel: 'Address suggestions',
    unavailable: 'Address suggestions are temporarily unavailable; enter the address manually.',
    noResults: 'No matching addresses found.',
    invalidSelection: 'This suggestion could not be resolved; enter the address manually.',
    incomplete: 'This address is incomplete; try refining it or enter it manually.',
    confirmPrompt: "We couldn't fully confirm this address. Did you mean:",
    acceptSuggestion: 'Yes, use this address',
    keepMyEntry: 'No, let me enter it manually',
    attribution: 'Address data by Google',
  },
};

var DEBOUNCE_MS = 350;
var MIN_QUERY_LENGTH = 3;

export default {
  name: 'GoogleAddressTypeahead',
  props: {
    countryCode: { type: String, required: true },
    suggestUrl: { type: String, required: true },
    validateUrl: { type: String, required: true },
    locale: { type: String, default: 'nl' },
    // 'public' (resources/js/app.js bundle - add-house.blade.php, properties/edit.blade.php)
    // uses the theme-input/theme-autocomplete/theme-btn classes (ThemeInput.vue/
    // ThemeAutocomplete.vue); 'admin' (resources/js/admin/app.js bundle - Modules/Property and
    // Modules/User admin forms) uses the different v-input/v-autocomplete/v-form__button classes
    // (VTextfield.vue/VAutocomplete.vue) - those two class sets are compiled into SEPARATE CSS
    // bundles (admin.css vs app.css) and are not interchangeable, so this component renders
    // genuinely different classes per theme rather than relying on one to happen to also match
    // the other's stylesheet.
    theme: { type: String, default: 'public' },
  },
  data() {
    return {
      query: '',
      suggestions: [],
      highlightedIndex: -1,
      showList: false,
      message: null,
      sessionToken: generateSessionToken(),
      requestToken: 0,
      lastSentQuery: null,
      debounceTimer: null,
      abortController: null,
      pendingConfirmation: null,
      instanceId: 'google-typeahead-' + Math.random().toString(36).slice(2),
    };
  },
  computed: {
    labels() {
      return MESSAGES[this.locale] || MESSAGES.nl;
    },
    listId() {
      return this.instanceId + '-list';
    },
    confirmationSummary() {
      // For a correction_requires_confirmation result, App\Services\Address\DTO\
      // AddressValidationResult::correctionRequiresConfirmation() puts the corrected address
      // under `suggested_address` and leaves `address` null (there was no separately-structured
      // "entered" address to compare against here - see that factory method's own docblock) -
      // reading `.address` here always crashed this computed property (TypeError: Cannot read
      // properties of null), which is why the confirm prompt would sometimes never even render.
      var a = this.pendingConfirmation && (this.pendingConfirmation.suggested_address || this.pendingConfirmation.address);
      if (!a) {
        return '';
      }
      return [a.street, a.house_number, a.postal_code, a.locality].filter(Boolean).join(', ');
    },
    // Only the confirm/reject buttons still vary by a plain class swap - the input/list
    // themselves need genuinely different DOM nesting per theme, not just different class
    // names, so those are two separate template branches above (see the template's own
    // docblock comment for why).
    classes() {
      if (this.theme === 'admin') {
        return {
          buttonPrimary: 'v-form__button v-form__button--is-success',
          buttonSecondary: 'v-form__button v-form__button--is-default',
        };
      }
      return {
        buttonPrimary: 'theme-btn theme-btn--theme theme-btn--small',
        // theme-btn alone (no color modifier) is transparent border + no background - real,
        // but visually indistinguishable from plain text. theme-btn--secondary gives it an
        // actual outlined, orange-text button shape instead.
        buttonSecondary: 'theme-btn theme-btn--secondary theme-btn--small',
      };
    },
  },
  watch: {
    countryCode() {
      // A different country invalidates whatever was mid-flight - never mix suggestions/a
      // pending confirmation from one country with a session token or query meant for another.
      this.resetSession();
    },
  },
  beforeDestroy() {
    clearTimeout(this.debounceTimer);
    if (this.abortController) {
      this.abortController.abort();
    }
  },
  methods: {
    optionId(index) {
      return this.instanceId + '-option-' + index;
    },
    resetSession() {
      clearTimeout(this.debounceTimer);
      if (this.abortController) {
        this.abortController.abort();
      }
      this.query = '';
      this.suggestions = [];
      this.showList = false;
      this.message = null;
      this.pendingConfirmation = null;
      this.lastSentQuery = null;
      this.sessionToken = generateSessionToken();
    },
    markUnavailable() {
      this.suggestions = [];
      this.showList = false;
      this.pendingConfirmation = null;
      this.message = this.labels.unavailable;
      this.$emit('unavailable');
    },
    onQueryInput() {
      this.pendingConfirmation = null;
      this.message = null;
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(this.runSuggest, DEBOUNCE_MS);
    },
    runSuggest() {
      var trimmed = (this.query || '').trim();
      if (trimmed.length < MIN_QUERY_LENGTH) {
        this.suggestions = [];
        this.showList = false;
        this.lastSentQuery = null;
        return;
      }
      if (trimmed === this.lastSentQuery) {
        return;
      }
      this.lastSentQuery = trimmed;

      if (this.abortController) {
        this.abortController.abort();
      }
      var controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
      this.abortController = controller;

      var token = ++this.requestToken;
      var self = this;

      fetch(this.suggestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-TOKEN': csrfToken(),
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ country_code: this.countryCode, q: trimmed, session_token: this.sessionToken }),
        signal: controller ? controller.signal : undefined,
      }).then(function (response) {
        return response.json().then(function (body) {
          return { httpStatus: response.status, body: body };
        });
      }).then(function (result) {
        if (token !== self.requestToken) {
          return;
        }
        self.handleSuggestResult(result.httpStatus, result.body);
      }).catch(function (error) {
        if (error && error.name === 'AbortError') {
          return;
        }
        if (token !== self.requestToken) {
          return;
        }
        self.markUnavailable();
      });
    },
    handleSuggestResult(httpStatus, body) {
      if (httpStatus !== 200 || !body) {
        this.markUnavailable();
        return;
      }
      if (body.status === 'unsupported_country' || body.status === 'provider_unavailable' || body.status === 'degraded') {
        this.markUnavailable();
        return;
      }
      if (body.status === 'empty' || !Array.isArray(body.data) || body.data.length === 0) {
        this.suggestions = [];
        this.showList = false;
        this.message = this.labels.noResults;
        return;
      }

      this.suggestions = body.data;
      this.highlightedIndex = -1;
      this.message = null;
      // Never auto-select a Google prediction, even when only one is returned - unlike PDOK's
      // exact-address resolve, a single Autocomplete prediction is still just a free-text guess
      // until Address Validation confirms it, so the user always makes the explicit pick.
      this.showList = true;
    },
    selectCandidate(candidate) {
      this.showList = false;
      this.query = candidate.label;
      this.suggestions = [];
      this.message = null;
      this.runValidate(candidate);
    },
    runValidate(candidate) {
      var self = this;

      fetch(this.validateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-TOKEN': csrfToken(),
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          country_code: this.countryCode,
          candidate_id: candidate.id,
          text: candidate.label,
          session_token: this.sessionToken,
        }),
      }).then(function (response) {
        return response.json().then(function (body) {
          return { httpStatus: response.status, body: body };
        });
      }).then(function (result) {
        if (result.httpStatus === 422) {
          self.message = self.labels.invalidSelection;
          return;
        }
        if (result.httpStatus !== 200 || !result.body) {
          self.markUnavailable();
          return;
        }
        self.handleValidateResult(result.body);
      }).catch(function () {
        self.markUnavailable();
      });
    },
    // `resolved` always emits the FLAT NormalizedAddress shape (postal_code, street,
    // house_number, ... plus resolution_id merged in) - never the wrapping
    // {status, address, suggested_address, resolution_id, ...} response envelope. Every consumer
    // of this event (add-house.blade.php, properties/edit.blade.php, the admin property forms,
    // InternationalAddressField.vue) reads fields directly off the emitted payload
    // (address.postal_code, address.street, ...), so emitting the envelope instead would leave
    // every field silently reading undefined - exactly the "fields don't fill in" bug this
    // flattening fixes. The actual address data sits under `address` for a verified result but
    // under `suggested_address` for a correction_requires_confirmation one (App\Services\
    // Address\DTO\AddressValidationResult::correctionRequiresConfirmation() leaves `address`
    // null there - see confirmationSummary()'s own comment) - both call sites below (a direct
    // verified result, and an explicitly accepted suggestion) go through this same helper, so
    // both need it to check the field that's actually populated.
    flattenedAddress(result) {
      var address = result.address || result.suggested_address;
      return Object.assign({}, address, { resolution_id: result.resolution_id });
    },
    handleValidateResult(result) {
      if (result.status === 'verified') {
        this.$emit('resolved', this.flattenedAddress(result));
        // Session concluded - Address Validation was called, so a fresh token is required for
        // the next session (Google: "once a session has concluded, the token is no longer
        // valid").
        this.sessionToken = generateSessionToken();
        return;
      }
      if (result.status === 'correction_requires_confirmation') {
        this.pendingConfirmation = result;
        // The session also concludes here (Address Validation was already called) - accepting
        // or rejecting below never re-validates, it only decides which address to emit.
        this.sessionToken = generateSessionToken();
        return;
      }
      if (result.status === 'incomplete' || result.status === 'unconfirmed') {
        this.message = this.labels.incomplete;
        this.sessionToken = generateSessionToken();
        return;
      }
      // unsupported_country / provider_unavailable
      this.markUnavailable();
      this.sessionToken = generateSessionToken();
    },
    acceptSuggestion() {
      if (!this.pendingConfirmation) {
        return;
      }
      this.$emit('resolved', this.flattenedAddress(this.pendingConfirmation));
      this.pendingConfirmation = null;
    },
    rejectSuggestion() {
      this.pendingConfirmation = null;
      this.$emit('correction-rejected');
    },
    onInputKeydown(event) {
      if (!this.showList || !this.suggestions.length) {
        return;
      }
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.highlightedIndex = (this.highlightedIndex + 1) % this.suggestions.length;
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.highlightedIndex = this.highlightedIndex <= 0
            ? this.suggestions.length - 1
            : this.highlightedIndex - 1;
          break;
        case 'Enter':
          if (this.highlightedIndex >= 0) {
            event.preventDefault();
            this.selectCandidate(this.suggestions[this.highlightedIndex]);
          }
          break;
        case 'Escape':
          this.showList = false;
          this.suggestions = [];
          break;
      }
    },
  },
};
</script>
